import React from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { useRadixSelectAnimation } from '../../hooks/useRadixSelectAnimation';
import { DROPDOWN_ANIMATION, DROPDOWN_BG, FORM_RADIUS, FORM_SHADOW, ITEM_HOVER } from './tokens';

export interface PillSelectOption {
  readonly value: string;
  readonly label: string;
}

interface PillSelectProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly PillSelectOption[];
  readonly placeholder?: string;
  readonly id?: string;
}

// Radix Select prohíbe `value=""` en un Item (lo reserva para "sin selección" internamente).
// Placeholder se modela como un Item real con este centinela, traducido a/desde '' en el borde.
const TODOS_VALUE = '__pillselect_todos__';

export const PillSelect: React.FC<PillSelectProps> = ({ value, onChange, options, placeholder, id }) => {
  const { status, handleOpenChange, handleAnimationEnd } = useRadixSelectAnimation();

  const radixValue = value === '' && placeholder ? TODOS_VALUE : value;
  const selectedLabel = radixValue === TODOS_VALUE ? placeholder : options.find(o => o.value === value)?.label;
  const isActive = Boolean(value);

  const handleValueChange = (v: string) => {
    onChange(v === TODOS_VALUE ? '' : v);
  };

  const contentClasses = [
    'z-[200] min-w-[8rem] overflow-hidden',
    FORM_RADIUS,
    FORM_SHADOW,
    DROPDOWN_ANIMATION,
    DROPDOWN_BG,
  ].join(' ');

  const itemClasses = [
    'relative flex items-center justify-between gap-2',
    'px-3 py-2 text-sm text-slate-700 rounded-lg cursor-pointer select-none outline-none',
    ITEM_HOVER,
    'data-[highlighted]:bg-slate-50',
    'data-[state=checked]:font-semibold data-[state=checked]:text-slate-900',
  ].join(' ');

  return (
    <RadixSelect.Root
      open={status !== 'closed'}
      onOpenChange={handleOpenChange}
      value={radixValue}
      onValueChange={handleValueChange}
    >
      <RadixSelect.Trigger
        id={id}
        className={`inline-flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 rounded-full text-sm font-medium border transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-500 data-[state=open]:border-slate-400 active:scale-[0.98] ${
          isActive
            ? 'bg-slate-800 text-white border-slate-800'
            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
        }`}
      >
        <RadixSelect.Value placeholder={placeholder}>{selectedLabel}</RadixSelect.Value>
        <RadixSelect.Icon asChild>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${isActive ? 'text-white/70' : 'text-slate-400'} ${status === 'closed' ? '' : 'rotate-180'}`} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      {status !== 'closed' && (
        <RadixSelect.Portal>
          <RadixSelect.Content
            position="popper"
            sideOffset={6}
            data-state={status === 'closing' ? 'closed' : 'open'}
            data-dropdown-portal="true"
            onAnimationEnd={handleAnimationEnd}
            className={`${contentClasses} dropdown-portal-protected`}
          >
            <RadixSelect.Viewport className="p-1">
              {placeholder && (
                <RadixSelect.Item value={TODOS_VALUE} className={itemClasses}>
                  <RadixSelect.ItemText>{placeholder}</RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator>
                    <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              )}
              {options.map((opt) => (
                <RadixSelect.Item
                  key={opt.value}
                  value={opt.value}
                  className={itemClasses}
                >
                  <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator>
                    <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      )}
    </RadixSelect.Root>
  );
};
